import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Pagination } from "../../../../components/ui";
import Table from "../../../../components/Table";
import { formatDateTime } from "../../../../util";

export const FETCH_EMAIL_ANALYTICS = gql`
  query FetchEmailAnalytics($id: Int!, $limit: Int!, $offset: Int!) {
    email(id: $id) {
      id
      title
      status
      ownerEmail
      meta
      analytics
      recipientAnalytics(limit: $limit, offset: $offset)
    }
  }
`;

const PAGE_SIZE = 25;
const dateLabel = value => (value ? formatDateTime(new Date(value)) : "—");
const Recipient = ({ values }) => (
  <div>
    <div>{values.name}</div>
    <div>{values.email}</div>
  </div>
);
const RecipientDate = ({ values, name }) => dateLabel(values[name]);
const RecipientDetails = ({ values }) => (
  <div className="whitespace-normal">
    {values.failureReason && <p>{values.failureReason}</p>}
    {values.unsubscribedAt && (
      <p>Unsubscribed: {dateLabel(values.unsubscribedAt)}</p>
    )}
    {values.complainedAt && (
      <p>Spam complaint: {dateLabel(values.complainedAt)}</p>
    )}
  </div>
);
const recipientHeaders = [
  { label: "Recipient", name: "email", type: "custom", component: Recipient },
  { label: "Delivery", name: "deliveryStatus", type: "label" },
  { label: "Opens", name: "opens", type: "label", defaultValue: "0" },
  {
    label: "First tracked open",
    name: "firstOpenedAt",
    type: "custom",
    component: RecipientDate,
  },
  {
    label: "Last tracked open",
    name: "lastOpenedAt",
    type: "custom",
    component: RecipientDate,
  },
  { label: "Clicks", name: "clicks", type: "label", defaultValue: "0" },
  {
    label: "Last tracked click",
    name: "lastClickedAt",
    type: "custom",
    component: RecipientDate,
  },
  {
    label: "Details",
    name: "details",
    type: "custom",
    component: RecipientDetails,
  },
];
const metrics = [
  ["Accepted", "accepted"],
  ["Delivered", "delivered"],
  ["Failed / bounced", "failed"],
  ["Unique tracked opens", "opened"],
  ["Total tracked opens", "totalOpens"],
  ["Tracked clicks", "clicked"],
  ["Unsubscribed", "unsubscribed"],
  ["Spam complaints", "complaints"],
];

const Analytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const { data, loading, error, refetch } = useQuery(FETCH_EMAIL_ANALYTICS, {
    variables: { id: Number(id), limit: PAGE_SIZE, offset },
    fetchPolicy: "cache-and-network",
    pollInterval: 30000,
    notifyOnNetworkStatusChange: true,
  });
  const email = data?.email;
  const analytics = email?.analytics;
  const recipients = email?.recipientAnalytics;

  return (
    <div className="mx-auto min-w-0 w-full px-4 py-5 sm:px-6 lg:px-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <h3 className="text-lg font-bold">
          Mailing Analytics{email?.title ? ` — ${email.title}` : ""}
        </h3>
        <div className="flex gap-3">
          <Button
            label={loading ? "Refreshing…" : "Refresh"}
            disabled={loading}
            onClick={() => refetch()}
          />
          <Button label="Close" onClick={() => navigate("/email")} />
        </div>
      </div>
      {error && (
        <div role="alert">
          <p>Unable to load email statistics. Please try Refresh.</p>
          <p className="mt-1 text-sm">{error.message}</p>
        </div>
      )}
      {loading && !email && <p role="status">Loading statistics…</p>}
      {!loading && !error && !email && <p>Email not found.</p>}
      {email && (
        <details className="mb-4">
          <summary className="cursor-pointer">Email content and owner</summary>
          <p>Owner: {email.ownerEmail || "Unknown"}</p>
          <p>Subject: {email.meta?.subject || "—"}</p>
          <p>Status: {email.status}</p>
          {email.meta?.body && (
            <iframe
              title="Email content preview"
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={email.meta.body}
              className="mt-3 h-96 w-full border"
            />
          )}
        </details>
      )}
      {analytics && (
        <>
          <p className="mb-4 text-sm text-gray-600">
            Counts are unique recipients except total tracked opens, which
            includes repeat opens. Delivered means accepted by the recipient’s
            mail server, not confirmed inbox placement. Opens and clicks are
            tracking signals, not proof of reading: privacy tools and security
            scanners can affect them.
          </p>
          {!analytics.eventCount && (
            <p role="status" className="mb-4">
              No tracking events received for this mailing yet. Events may take
              a few minutes to arrive after sending.
            </p>
          )}
          <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
            {metrics.map(([label, key]) => (
              <div key={key} className="rounded border-2 px-4 py-5 text-center">
                <div className="text-3xl">{analytics[key] || 0}</div>
                <div>{label}</div>
              </div>
            ))}
          </div>
          <div className="mb-5 text-sm text-gray-600">
            <p>Latest event: {dateLabel(analytics.lastEventAt)}</p>
            <p>Last tracked open: {dateLabel(analytics.lastOpenedAt)}</p>
            <p>
              Refreshes automatically every 30 seconds. Test sends are excluded.
            </p>
          </div>
        </>
      )}
      {recipients && (
        <>
          <h4 className="mb-3 font-bold">Recipients</h4>
          <Table
            headers={recipientHeaders}
            rows={recipients.edges}
            loading={loading}
            variant="dynamicHeight"
            allowHorizontalScroll
            emptyMessage="No recipients."
          />
          <div className="mt-4">
            <Pagination
              count={recipients.count}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={({ offset: nextOffset }) => setOffset(nextOffset)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
